import React, { useState, useEffect } from "react";
import { Alert, Button } from "react-bootstrap";
import { getStorage, ref, listAll, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "../firebase-config";

// Image Picker Component for Firebase Storage
const ImagePicker = ({ selectedImageUrl, onImageSelect }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      setLoading(true);
      const storage = getStorage(firebaseApp);
      const imagesRef = ref(storage, "Night Lesson Images");

      const result = await listAll(imagesRef);
      const imageUrls = await Promise.all(
        result.items.map(async (imageRef) => {
          const url = await getDownloadURL(imageRef);
          return {
            name: imageRef.name,
            url: url,
            path: imageRef.fullPath,
          };
        })
      );

      setImages(imageUrls);
      setError(null);
    } catch (err) {
      console.error("Error loading images:", err);
      setError("Failed to load images from Firebase Storage");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px 20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
        }}
      >
        <p style={{ color: "#6c757d", marginBottom: "0" }}>
          Loading images from Firebase Storage...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}
        <br />
        <Button
          variant="outline-danger"
          size="sm"
          onClick={loadImages}
          className="mt-2"
        >
          Retry
        </Button>
      </Alert>
    );
  }

  if (images.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px 20px",
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          border: "1px solid #e9ecef",
        }}
      >
        <p style={{ color: "#6c757d", marginBottom: "0" }}>
          No images found in the Night Lesson Images folder.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Selected Image Display */}
      {selectedImageUrl && (
        <div className="mb-3">
          <h6
            style={{ fontWeight: "600", color: "#495057", marginBottom: "8px" }}
          >
            Selected Image:
          </h6>
          <div
            style={{
              border: "2px solid #F0D6B5",
              borderRadius: "8px",
              padding: "8px",
              backgroundColor: "#fff",
              display: "inline-block",
            }}
          >
            <img
              src={selectedImageUrl}
              alt="Selected lesson image"
              style={{
                maxWidth: "200px",
                maxHeight: "120px",
                objectFit: "cover",
                borderRadius: "4px",
              }}
            />
          </div>
        </div>
      )}

      {/* Image Grid */}
      <div
        style={{
          maxHeight: "300px",
          overflowY: "auto",
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#fafafa",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
            gap: "12px",
          }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              onClick={() => onImageSelect(image.url)}
              style={{
                cursor: "pointer",
                border:
                  selectedImageUrl === image.url
                    ? "3px solid #F0D6B5"
                    : "2px solid #e9ecef",
                borderRadius: "8px",
                padding: "4px",
                backgroundColor: "#fff",
                transition: "all 0.2s ease",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                if (selectedImageUrl !== image.url) {
                  e.target.style.borderColor = "#F0D6B5";
                  e.target.style.transform = "scale(1.02)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedImageUrl !== image.url) {
                  e.target.style.borderColor = "#e9ecef";
                  e.target.style.transform = "scale(1)";
                }
              }}
            >
              <img
                src={image.url}
                alt={image.name}
                style={{
                  width: "100%",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
              <div
                style={{
                  fontSize: "10px",
                  color: "#6c757d",
                  textAlign: "center",
                  marginTop: "4px",
                  wordBreak: "break-word",
                  lineHeight: "1.2",
                }}
              >
                {image.name.replace(/\.[^/.]+$/, "")}{" "}
                {/* Remove file extension */}
              </div>
              {selectedImageUrl === image.url && (
                <div
                  style={{
                    position: "absolute",
                    top: "4px",
                    right: "4px",
                    backgroundColor: "#F0D6B5",
                    color: "#635956",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                >
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {images.length > 0 && (
        <div className="mt-2" style={{ fontSize: "12px", color: "#6c757d" }}>
          {images.length} image{images.length !== 1 ? "s" : ""} available •
          Click to select
        </div>
      )}
    </div>
  );
};

export default ImagePicker;
